import { IPackagesResponse, IPackagesPageResponse, IPackagesSummaryResponse, IPackageUsersResponse, ICarrierUsersResponse, IPackageGroupMeResponse, ICarrierManagerStatusResponse, IDealershipGroupsResponse, ICarrierManagerResponse } from "@types";
import api from ".";

export const fetchPackagesPage = async (
  page: number = 0,
  size: number = 10,
  search?: string,
  sort?: string,
  sortBy?: string,
  status?: string,
  startDate?: string,
  endDate?: string
) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());

  if (search) {
    params.append("search", search);
  }

  if (sort) {
    params.append("sort", sort.toUpperCase());
  }

  if (sortBy) {
    params.append("sortBy", sortBy);
  }

  if (status) {
    params.append("status", status.toUpperCase());
  }

  if (startDate) {
    params.append("startDate", startDate);
  }

  if (endDate) {
    params.append("endDate", endDate);
  }

  const url = `/v1/routes/packages/page?${params.toString()}`;
  const response = await api.get<IPackagesPageResponse>(url);

  return response.data;
};

export const fetchPackagesSummary = async () => {
  const response = await api.get<IPackagesSummaryResponse>("/v1/routes/packages/summary");
  return response.data;
};

export const confirmPackageDelivery = async (packageId: string, receivedById?: string) => {
  const response = await api.patch(`/v1/routes/packages/${packageId}/status?action=confirm`, {
    receivedById,
  });
  return response.data;
};

export const cancelPackageDelivery = async (packageId: string, canceledById?: string) => {
  const response = await api.patch(`/v1/routes/packages/${packageId}/status?action=cancel`, {
    canceledById,
  });
  return response.data;
};

export const fetchPackageUsers = async (search?: string) => {
  const params = new URLSearchParams();

  if (search) {
    params.append("search", search);
  }

  const url = `/v1/routes/packages/users${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get<IPackageUsersResponse>(url);
  return response.data;
};

export const fetchCarrierUsers = async () => {
  const response = await api.get<ICarrierUsersResponse>("/v1/routes/packages/groups/carrier/users");
  return response.data;
};

export interface ICreatePackagePayload {
  nf: string;
  name: string;
  createdById?: string;
  responsible: {
    name: string;
    profileId: string;
  };
  dealerResponsible: { name: string; profileId: string } | null;
  superior1: { name: string; profileId: string } | null;
  superior2: { name: string; profileId: string } | null;
  superior3: { name: string; profileId: string } | null;
  status: string;
  packageReceived: null;
  canceledAt: null;
  shipping: {
    date: string;
    etaDate: string;
  };
  destination: {
    dn: string;
    dealerName: string;
    cnpj: string;
    location: string;
  };
  carrierName?: string;
}

export const createPackage = async (payload: ICreatePackagePayload) => {
  const response = await api.post("/v1/routes/packages", payload);
  return response.data;
};

export interface IPackageDetailResponse {
  data: {
    message: string;
    result: {
      id: string;
      nf: string;
      name: string;
      dealerResponsibleName?: string | null;
      dealerResponsibleProfileId?: string | null;
      responsibleName: string;
      responsibleProfileId: string;
      superior1ProfileId: string | null;
      superior2ProfileId: string | null;
      superior3ProfileId: string | null;
      createdAt: string;
      createdById?: string | null;
      receivedAt: string | null;
      receivedById?: string | null;
      receivedByName?: string | null;
      deletedAt: string | null;
      canceledAt: string | null;
      canceledById?: string | null;
      status: string;
      shipping: {
        date: string;
        etaDate: string;
      } | null;
      destination: {
        dn: string;
        dealerName: string;
        cnpj: string;
        location: string;
      } | null;
      carrierName?: string | null;
    };
  };
}

export const fetchPackageById = async (packageId: string) => {
  const response = await api.get<IPackageDetailResponse>(`/v1/routes/packages/${packageId}`);
  return response.data;
};

export const markPackageNotReceived = async (packageId: string) => {
  const response = await api.patch(`/v1/routes/packages/${packageId}`, {
    packageReceived: false,
    status: "DELAYED",
  });
  return response.data;
};

export const updatePackageEtaDate = async (packageId: string, etaDate: string) => {
  const response = await api.patch(`/v1/routes/packages/${packageId}`, {
    shipping: { etaDate },
  });
  return response.data;
};

export interface IBatchUploadResult {
  id?: string;
  totalRecords: number;
  successCount: number;
  errorCount: number;
  errors?: Array<{ line?: number; message?: string }> | null;
  errorDetails?: string | null;
  createdPackageIds?: string[];
  status: string;
  logId?: string;
  fileName?: string;
  percentage?: number;
  progress?: number;
  startedAt?: string;
  finishedAt?: string;
}

export interface IBatchUploadResponse {
  status: string;
  data: {
    message: string;
    result: IBatchUploadResult;
  };
}

export interface IBatchLogResponse {
  status: string;
  data: {
    message: string;
    result: IBatchUploadResult;
  };
}

export const uploadBatchPackages = async (notfis: File, conemb: File) => {
  const formData = new FormData();
  formData.append("notfis", notfis);
  formData.append("conemb", conemb);

  const response = await api.post<IBatchUploadResponse>("/v1/routes/packages/batch/config/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const fetchBatchLog = async (logId: string) => {
  const response = await api.get<IBatchLogResponse>(`/v1/routes/packages/batch/config/logs/${logId}`);
  return response.data;
};

export interface IBatchLogsListResponse {
  status: string;
  data: {
    message: string;
    result: {
      content: IBatchUploadResult[];
      totalElements: number;
    };
  };
}

export const fetchBatchLogsList = async (page: number = 0, size: number = 1) => {
  const response = await api.get<IBatchLogsListResponse>(`/v1/routes/packages/batch/config/logs?page=${page}&size=${size}`);
  return response.data;
};

export const fetchPackageGroupMe = async () => {
  const response = await api.get<IPackageGroupMeResponse>("/v1/routes/packages/groups/me");
  return response.data;
};

export const fetchCarrierManagerStatus = async () => {
  const response = await api.get<ICarrierManagerStatusResponse>("/v1/routes/packages/groups/carrier/manager/status");
  return response.data;
};

export const assignCarrierManager = async (userRioId: string) => {
  const response = await api.post<ICarrierManagerResponse>("/v1/routes/packages/groups/carrier/manager", {
    userRioId,
  });
  return response.data;
};

export const assignCarrierManagers = async (userRioIds: string[]) => {
  const response = await api.post<ICarrierManagerResponse>("/v1/routes/packages/groups/carrier/manager", {
    userRioIds,
  });
  return response.data;
};

export const updateCarrierManager = async (userRioId: string) => {
  const response = await api.put<ICarrierManagerResponse>("/v1/routes/packages/groups/carrier/manager", {
    userRioId,
  });
  return response.data;
};

export const fetchDealerships = async () => {
  const response = await api.get<IDealershipGroupsResponse>("/v1/routes/packages/groups/dealerships");
  return response.data;
};
